import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MoreVertical, Trash } from "lucide-react"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@f2e/ui/components/base"
import { ellipsis } from "@f2e/shared/utils"

import { PermissionMenuItem } from "@/components/Auth"
import { isOwner } from "@/lib/utils"
import { Member, Permission } from "@/types"

export interface ActionButtonProps {
  className?: string
  member: Member
  onRemove?: (member: Member) => void
}

const ActionButton = ({ className, member, onRemove }: ActionButtonProps) => {
  const { t } = useTranslation()
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const handleDeleteMenuItemClick = () => {
    setShowDeleteDialog(true)
  }

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
          <PermissionMenuItem
            permission={Permission.OrgRemoveMembers}
            disabledTooltip={t("common.noPermission")}
            className="text-red-500"
            disabled={isOwner(member.role)}
            onClick={handleDeleteMenuItemClick}
          >
            <Trash className="w-4 h-4 mr-2" />
            Remove
          </PermissionMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Do you really want to REMOVE this user?
            </AlertDialogTitle>
            <AlertDialogDescription>
              User "{ellipsis(member.name, 54)}" will be removed from the
              organization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => onRemove?.(member)}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export { ActionButton }
export default ActionButton
